import React, { useState } from "react";
import classNames from "classnames";
import { Flex, Text } from "@jsq/ds/core";
import { Button } from "@jsq/ds/deprecated";
import { Sizes } from "@jsq/ds/styles";
import ContactSupportModal from "./ContactSupportModal";
import { container, image, icon, caption, button } from "./styles_vanilla.css";

export enum ErrorType {
  InternalError = "500",
  Offline = "offline",
  NotFound = "404",
  NoAccess = "NoAccess",
  PreviewPortalNoAccess = "PreviewPortalNoAccess",
  DisregardedEntityQuestionnaireNotFound = "DisregardedEntityQuestionnaireNotFound",
}

type Props = {
  error: ErrorType;
  hideContainer?: boolean;
};

export const ErrorPage: React.FunctionComponent<Props> = (props) => {
  const [showSupportModal, setShowSupportModal] = useState(false);
  const refresh = () => {
    window.location.reload();
  };

  const errorInfo = {
    "500": {
      title: "This page couldn't be loaded",
      body: "An internal error occurred and we are unable to retrieve the page you're looking for. Try refreshing the page in a few minutes, or contact support.",
      imgUrl: "/static/error/500_error.svg",
    },
    offline: {
      title: "Not connected to the internet",
      body: "Your device appears to be offline, and can't access Juniper Square. Check your network connection and refresh this page.",
      imgUrl: "/static/error/offline.svg",
    },
    "404": {
      title: "There's nothing here",
      body: "This page doesn't exist, or you don't have permission to view it. Contact your team's admin to confirm your permissions, or reach out to support.",
      imgUrl: "/static/error/404_error.svg",
    },
    NoAccess: {
      title: "You don't have access to this page",
      body: "If you require access, contact your team's administrator to confirm your permissions.",
      imgUrl: "/static/error/404_error.svg",
    },
    PreviewPortalNoAccess: {
      title: "You do not have access to preview the portal of this contact",
      body: "Apologies for the inconvenience. If you require access, contact your team's administrator to confirm your permissions.",
      imgUrl: "/static/error/404_error.svg",
    },
    DisregardedEntityQuestionnaireNotFound: {
      title: "This questionnaire cannot be found",
      body: "You may have already completed this questionnaire or have no relevant investment accounts. Please contact investor@fossalimitedpartners for assistance",
      imgUrl: "/static/error/404_error.svg",
    },
  };

  const contactSupportButton = (
    <Button
      className={button}
      variant="neutral"
      onClick={() => setShowSupportModal(true)}
      key="ErrorPage_support"
    >
      Contact support
    </Button>
  );

  const getButtons = (error: ErrorType) => {
    switch (error) {
      case ErrorType.Offline:
        return [
          <Button onClick={refresh} key="ErrorPage_refresh" className={button}>
            <span className={classNames("glyphicon", "glyphicon-refresh", icon)} />
            Refresh
          </Button>,
        ];
      case ErrorType.NotFound:
        return [
          <Button key="ErrorPage_home" href="/dashboard" className={button}>
            Back to home
          </Button>,
          contactSupportButton,
        ];
      case ErrorType.InternalError:
        return [
          <Button onClick={refresh} className={button} key="ErrorPage_refreshPage">
            Refresh page
          </Button>,
          contactSupportButton,
        ];
      default:
        return [];
    }
  };

  return (
    <Flex
      className={classNames(!props.hideContainer && container)}
      flexDirection="column"
      justifyContent="center"
      flexGrow={1}
      gap={Sizes.XL}
    >
      <Flex justifyContent="center">
        <img
          src={errorInfo[props.error].imgUrl}
          alt={errorInfo[props.error].title}
          className={image}
        />
      </Flex>
      <Flex flexDirection="column" gap={Sizes.MD}>
        <Flex justifyContent="center" flexGrow={1}>
          <Text variant="h3">{errorInfo[props.error].title}</Text>
        </Flex>
        <Flex justifyContent="center" flexGrow={1}>
          <Text variant="regular-md" className={caption}>
            {errorInfo[props.error].body}
          </Text>
        </Flex>
      </Flex>
      <Flex justifyContent="center">{getButtons(props.error).map((button) => button)}</Flex>
      <ContactSupportModal show={showSupportModal} onHide={() => setShowSupportModal(false)} />
    </Flex>
  );
};
